import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { Layout } from '../components/layout/Layout';
import { MATCH_TYPES, MATCH_TYPE_LABEL, SOUND_MATCH_CATEGORIES } from '../data/matching';
import '../styles/sequencing-generator.css';
import '../styles/curriculum-manager.css';

// ═══════════════════════════════════════════════════════════════════════════
// Create Matching Activity — manual entry, no AI involved.
// Creates a draft document directly in Firestore, then hands off to the
// Matching Editor (/matching/review/:deckId) to add trials and images.
// ═══════════════════════════════════════════════════════════════════════════

export const MatchingGeneratorPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [title,        setTitle]        = useState('');
  const [description,  setDescription]  = useState('');
  const [category,     setCategory]     = useState('');
  const [subcategory,  setSubcategory]  = useState('');
  const [matchType,    setMatchType]    = useState(MATCH_TYPES.IDENTICAL);
  const [tagsInput,    setTagsInput]    = useState('');

  const [existingCategories, setExistingCategories] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error,    setError]    = useState(null);

  // ── Pull existing category names for the datalist suggestions ────────────
  useEffect(() => {
    getDocs(query(collection(db, 'generatedMatchingDecks'), orderBy('category')))
      .then(snap => {
        const cats = [...new Set(snap.docs.map(d => d.data().category).filter(Boolean))];
        setExistingCategories(cats);
      })
      .catch(() => {});
  }, []);

  const canSubmit = title.trim().length > 0 && category.trim().length > 0;

  // When authoring a sound deck, surface curated category ideas (animal
  // sounds, safety sounds, letter sounds…) alongside whatever categories
  // already exist in Firestore.
  const categorySuggestions = matchType === MATCH_TYPES.SOUND
    ? [...new Set([...SOUND_MATCH_CATEGORIES, ...existingCategories])]
    : existingCategories;

  const handleCreate = async () => {
    if (!canSubmit || creating) return;
    setCreating(true);
    setError(null);

    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);

      const docRef = await addDoc(collection(db, 'generatedMatchingDecks'), {
        createdBy:   currentUser.uid,
        status:      'draft',
        title:       title.trim(),
        description: description.trim(),
        category:    category.trim(),
        subcategory: subcategory.trim(),
        matchType,
        tags,
        trials:      [],
        trialCount:  0,
        createdAt:   serverTimestamp(),
        updatedAt:   serverTimestamp(),
      });

      navigate(`/matching/review/${docRef.id}`);
    } catch (err) {
      setError(err.message || 'Could not create the activity. Please try again.');
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div className="gen-page">

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="gen-header">
          <div className="gen-header-icon">🧩</div>
          <div>
            <h1 className="gen-title">Create Matching Activity</h1>
            <p className="gen-subtitle">
              Fill in the basic details, then add matching trials and upload images on the next screen.
            </p>
          </div>
        </div>

        <div className="gen-input-card">

          {/* ── Title ────────────────────────────────────────────────── */}
          <label className="gen-label">Title</label>
          <input
            className="gen-input"
            style={{ fontSize: 16, marginBottom: 20 }}
            placeholder="e.g., Match the Farm Animals"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />

          {/* ── Description ──────────────────────────────────────────── */}
          <label className="gen-label">Description</label>
          <textarea
            className="gen-input"
            rows={2}
            style={{ marginBottom: 20, resize: 'vertical' }}
            placeholder="Brief description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />

          {/* ── Category / Subcategory ───────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
            <div>
              <label className="gen-label">Category</label>
              <input
                className="gen-input"
                list="matching-category-list"
                placeholder="e.g., Animals"
                value={category}
                onChange={e => setCategory(e.target.value)}
              />
              <datalist id="matching-category-list">
                {categorySuggestions.map(c => <option key={c} value={c} />)}
              </datalist>
              {matchType === MATCH_TYPES.SOUND && (
                <p style={{ fontSize: 11, color: '#9CA3AF', margin: '6px 0 0' }}>
                  Ideas: {SOUND_MATCH_CATEGORIES.join(', ')}
                </p>
              )}
            </div>
            <div>
              <label className="gen-label">Subcategory</label>
              <input
                className="gen-input"
                placeholder="optional"
                value={subcategory}
                onChange={e => setSubcategory(e.target.value)}
              />
            </div>
          </div>

          {/* ── Match type ───────────────────────────────────────────── */}
          <label className="gen-label">Match Type</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {Object.values(MATCH_TYPES).map(type => (
              <button
                key={type}
                type="button"
                onClick={() => setMatchType(type)}
                style={{
                  padding: '7px 14px', borderRadius: 20, border: '1.5px solid',
                  borderColor: matchType === type ? '#8B2635' : '#E5E7EB',
                  background:  matchType === type ? '#8B263518' : '#F9FAFB',
                  color:       matchType === type ? '#8B2635' : '#6B7280',
                  fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                }}
              >
                {MATCH_TYPE_LABEL[type]}
              </button>
            ))}
          </div>

          {/* ── Tags ─────────────────────────────────────────────────── */}
          <label className="gen-label">Tags</label>
          <input
            className="gen-input"
            style={{ fontSize: 14, marginBottom: 28 }}
            value={tagsInput}
            onChange={e => setTagsInput(e.target.value)}
            placeholder="comma, separated, tags (optional)"
            disabled={creating}
          />

          {/* ── Submit ───────────────────────────────────────────────── */}
          <button
            className="gen-cta"
            onClick={handleCreate}
            disabled={!canSubmit || creating}
          >
            {creating
              ? <><span className="gen-spinner" /> Creating…</>
              : '+ Create Draft & Add Trials'
            }
          </button>

          {error && <p className="gen-error">{error}</p>}

          {!canSubmit && (
            <p style={{ textAlign: 'center', fontSize: 12, color: '#D1D5DB', marginTop: 12 }}>
              Title and category are required to continue
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
};
